import type { IGetTableInfoDto, ITableColumnDto } from '@dbl/services/dataSource';
import {ITableJoinRelation} from '@/pages/datalab/pages/Multidimensional/Analysis/components/HeaderData/interface';
import type { IColumnInfo, IDimensionRange, TFieldType } from '@dbl/core/dataSource';
import type { TChartCalculateMode } from '@dbl/core/chartEditor';

export interface TagsColumn {
  id: string;
  content: string;
  column?: string;
  type?: string;
  is_sensitive?: boolean; // 是否敏感字段
  table_code?: string;
  table_id?: string;
}

// 维度映射表
export interface DimensionMapItem {
  name: string;
  icon: string;
  values: string[];
}

export interface DimensionMapInfo {
  options: [{value:'';}] | [];
  data: DimensionMapItem[];
  loading: boolean;
}

// 自定义字段
export interface CustomColumn {
  /** 保存/上传用英文字段名；逻辑唯一身份请用 name_cn（与 DefineField 写入时通常等于 name_cn） */
  name_en: string;
  /** 自定义字段唯一身份（进 QB 的 column_name、配置引用、字段排序 key 均用此值） */
  name_cn: string;
  sql_expression: string; // sql表达式
  column_type: TFieldType; // 字段类型  number string date
  remark?: string; // 备注
  accuracy?: number; // 小数位数
  id: string; // 唯一标志
  column_custom: boolean; // true代表是自定义字段
  to_date_format?: string; // 转换后的日期格式
  date_format?: string; // 转换后的日期格式
  is_aggregate?: boolean; // 自定义sql中是否包含聚合函数
  isAll?: boolean; // 是否是全选
  unit?: string; // 数据展示bmk还是千分隔符
}

export function convertCustomColumnToColumnRawInfo(customColumn: CustomColumn): IColumnInfo & ITableColumnDto {
  return {
    ...customColumn,
    table_id: '',
    table_code: '',
    // 进 QB / 配置引用唯一身份：name_cn（name_en 仅保留展示与上传）
    column_name: customColumn.name_cn,
    column_display: customColumn.name_cn,
    column_display_en: customColumn.name_en,
    display_name: customColumn.name_cn,
    display_name_en: customColumn.name_en,
    column_remark: customColumn.remark || '',
    column_type: customColumn.column_type,
    accuracy: customColumn.accuracy,
    column_sensitive: false,
    column_granularity: 'day',
    column_open: false,
    column_custom: true,
    options_now: [],
    options_all: [],
    is_aggregate: /\b(avg|sum|distinct|max|min|count)\s*\(/i.test(customColumn.sql_expression),
    isAll: true,
  };
}

export interface FiltersItem {
  table_id: string;
  table_code: string;
  column: string;
  filter_key?: string;
  operation: string;
  value: any;
  connect_method?: number; // 0:参与外层filters_connect_type 的连接方式 1:不参与
  filter_method?: number;
  column_type: string;
  attribute: number | undefined;
  accuracy: number | undefined;
  flag: number | undefined;
  dimension_setting: string | undefined;
  date_format: string | undefined;
}

export interface MetricsItem {
  table_id: string;
  table_code: string;
  aggregate: string;
  column: string;
  label: string;
  column_type: string;
  alias?: string; // 字段用在统计指标情况下的别名
  filters?: AnalysisFilters;
}

export interface OrderByItem {
  table_id: string;
  column: string;
  label: string;
  sort_model: string; // desc：倒序 asc: 正序
}

export interface Condition {
  game_code: string;
  group_by: GroupBy[];
  filters: AnalysisFilters;
  filter_compares?: AnalysisFilters[];
  metrics: MetricsItem[];
  orderby: OrderByItem[];
  custom_columns?: CustomColumn[];
  rowLimit: string | number;
  query_mode: TChartCalculateMode;
}

export interface AnalysisFilters {
  filters: FiltersItem[];
  filters_connect_type?: number;
}

export interface GroupBy {
  table_id: string;
  column: string;
  label: string;
  column_type: string;
  type?: string; // 分类方式  default:离散  range:区间  nolyAnalysis:只参与计算，界面不展示，从保存中返回现场时需过滤掉
  range?: IDimensionRange[];
}

export interface Analysis {
  table_infos?: ITableJoinRelation[];
  queries: Condition;
  max_size: number;
  pagesize?: number;
  page?: number;
  is_chart: boolean;
  is_download: boolean;
  query_sequence_id?: string;
  cache_key?: string;
  data_package_infos?: AnalysisPackageInfo[];
  data_tables?: IGetTableInfoDto[];
  chart_type?: string;
  // 图表配置参数
  customizeSetting?: {[key in string]: unknown};
  // 是否请求展示维度选项
  get_dimensions: boolean;
  sorts?: Array<{code: string; order: "asc" | "desc";}>;
}

export interface AnalysisPackageInfo {
  package_uuid: string;
  relate_column?: AnalysisColumnInfo[];
}

export interface AnalysisColumnInfo {
  table_id: string;
  column: string;
  table_code: string;
}

// 表格的表头字段
export interface FormColumn {
  name: string; // 表头名称
  name_en: string; // 表头英文名称
  can_sort: boolean; // 是否需要排序 true:需要  false:不需要
  type: number; // 标识字段类型 指标项(metrics): 0 聚合项(groupby): 1  对比项(compare): 2
  column_date_type: string;
  table_code: string; // t0 t1 t2
  origin_name: string; // 原始字段名称
}

// 编辑字段接口上传参数
export interface UpdateTableFieldRequest {
  table_id: string;
  lan: string; // zh en
  customize_table_name?: string;
  table_desc?: string;
  sub_label?: string;
  label?: string;
  cycle?: string;
  columns: UpdateColumnInfo[];
}

export interface UpdateColumnInfo {
  column_name: string;
  display_name?: string; // 重命名上传的字段 (同时是右侧字段显示的中文名称)
  column_type?: string; //  number date string time float percent
  attribute?: number; //  0: 公共维度， 1: 普通维度 2: 指标 8: 敏感字段
  accuracy?: number; // 小数点保留位数
  flag?: number; // 是否包含255 0: 包含 1:不包含
  remark?: string; // 字段说明
  dimension_setting?: string; //  origin: 原维度值 system: 系统自动匹配 customize: 自定义
  is_dimension?: boolean; // 是否是维度
  isCommonDimension?: boolean; // 是否是公共维度
  is_common_dimension?: boolean; // 是否是公共维度
  is_metric?: boolean; // 是否是指标
  column_display_en?: string; // 左侧字段显示的英文名称
  column_display?: string; // 左侧字段显示的中文名称
  display_name_en?: string; // 右侧字段显示的英文名称
  // 为了兼容不同类型的对象, 所以参数上有所重复
}

